/**
 * FreeSoulz motion — native, isolated, no libraries.
 *
 * Verbs: arrive, separate, focus, reflect, settle, confirm.
 * Load only from layout/fs.liquid. Do not attach to theme.js or the reader.
 *
 * Arrival ceremonies happen once. Scroll choreography is reversible and
 * section-specific so direction remains legible without hijacking input.
 * Copy stays calm; app screens, books, and grouped cards carry the depth.
 */
(function () {
  'use strict';

  var root = document.body;
  if (!root || !root.hasAttribute('data-fs')) return;

  var reduce =
    root.getAttribute('data-disable-animations') === 'true' ||
    (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var nativeScrollTimelines =
    window.CSS &&
    CSS.supports &&
    CSS.supports('animation-timeline: view()');

  var familyLocks = {};

  function arrive(el) {
    if (!el || el.classList.contains('is-in')) return;
    el.classList.remove('is-pending');
    el.classList.add('is-in');
    if (el.hasAttribute('data-fs-light')) {
      el.classList.add('is-lit');
    }
    var lights = el.querySelectorAll('[data-fs-light]');
    for (var i = 0; i < lights.length; i += 1) {
      lights[i].classList.add('is-lit');
    }
  }

  function revealAll() {
    var nodes = document.querySelectorAll('[data-fs-motion], [data-fs-light]');
    for (var i = 0; i < nodes.length; i += 1) {
      arrive(nodes[i]);
    }
  }

  function holdFor(el) {
    if (el.hasAttribute('data-fs-delay')) return 0;
    var rank = el.getAttribute('data-fs-rank');
    var verb = el.getAttribute('data-fs-motion');
    if (verb === 'light') return 0;
    if (el.hasAttribute('data-fs-object')) return 160;
    if (rank === 'vow') return 260;
    if (rank === 'chamber') return 140;
    return 90;
  }

  function shouldObserve(el) {
    if (el.hasAttribute('data-fs-motion')) return true;
    if (el.hasAttribute('data-fs-light')) {
      return !el.closest('[data-fs-motion]');
    }
    return false;
  }

  function schedule(el, io) {
    if (!el || el.classList.contains('is-in')) return;

    var family = el.getAttribute('data-fs-family');
    var members;
    var i;

    if (family) {
      if (familyLocks[family]) return;
      familyLocks[family] = true;
      members = document.querySelectorAll('[data-fs-family="' + family + '"]');
      if (io) {
        for (i = 0; i < members.length; i += 1) {
          io.unobserve(members[i]);
        }
      }
      window.setTimeout(function () {
        for (var m = 0; m < members.length; m += 1) {
          arrive(members[m]);
        }
      }, holdFor(el));
      return;
    }

    if (io) io.unobserve(el);
    window.setTimeout(function () {
      arrive(el);
    }, holdFor(el));
  }

  function observeArrivals() {
    var nodes = document.querySelectorAll('[data-fs-motion], [data-fs-light]');
    var watch = [];
    var i;
    for (i = 0; i < nodes.length; i += 1) {
      if (!shouldObserve(nodes[i])) continue;

      /*
       * The hero is already the first room. Waiting for a large intersection
       * ratio can leave its phone composition invisible on short viewports.
       * Start its restrained entrance immediately; CSS still owns the stagger.
       */
      if (nodes[i].closest('[data-fs-bookend="hero"]')) {
        schedule(nodes[i], null);
      } else {
        watch.push(nodes[i]);
      }
    }
    if (!watch.length) return;

    if (!('IntersectionObserver' in window)) {
      revealAll();
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        for (var e = 0; e < entries.length; e += 1) {
          var entry = entries[e];
          if (!entry.isIntersecting) continue;
          schedule(entry.target, io);
        }
      },
      { threshold: 0.16, rootMargin: '0px 0px -10% 0px' }
    );

    for (i = 0; i < watch.length; i += 1) {
      io.observe(watch[i]);
    }
  }

  function observeScenes() {
    if (!('IntersectionObserver' in window)) return;

    /*
     * One engine, different choreography. Each profile describes the physical
     * metaphor of its room; no blanket reveal preset is applied to the page.
     * The room itself remains calm; the product objects carry the cinematic
     * depth. Fallback values are still visible enough to make scroll direction
     * legible on browsers without native view timelines.
     */
    var sceneProfiles = [
      { selector: '.fs-why__inner', className: 'fs-motion-editorial', enterX: -44, enterY: 30, enterZ: -70, enterRX: 0.7, enterRY: -2.2, exitX: 22, exitY: -18, exitZ: -38, exitRX: -0.45, exitRY: 1.1, enterScale: -0.035, exitScale: -0.018, opacityFloor: 0.76 },
      { selector: '.fs-silent__inner', className: 'fs-motion-cost', enterX: 0, enterY: 54, enterZ: -96, enterRX: 2.1, enterRY: 0, exitX: 0, exitY: -24, exitZ: -54, exitRX: -0.9, exitRY: 0, enterScale: -0.055, exitScale: -0.028, opacityFloor: 0.72 },
      { selector: '.fs-standard__inner', className: 'fs-motion-vow', enterX: 0, enterY: 38, enterZ: -110, enterRX: 0.8, enterRY: 0, exitX: 0, exitY: -20, exitZ: -44, exitRX: -0.5, exitRY: 0, enterScale: -0.045, exitScale: -0.02, opacityFloor: 0.74 },
      { selector: '.fs-pillars', className: 'fs-motion-system', enterX: 0, enterY: 68, enterZ: -90, enterRX: 2.3, enterRY: 0, exitX: 0, exitY: -28, exitZ: -52, exitRX: -1.1, exitRY: 0, enterScale: -0.052, exitScale: -0.025, opacityFloor: 0.72 },
      { selector: '.fs-practice', className: 'fs-motion-plan', enterX: 32, enterY: 52, enterZ: -92, enterRX: 1.25, enterRY: 1.8, exitX: -15, exitY: -24, exitZ: -46, exitRX: -0.7, exitRY: -0.8, enterScale: -0.042, exitScale: -0.02, opacityFloor: 0.74 },
      { selector: '.fs-protection', className: 'fs-motion-protect', enterX: -34, enterY: 50, enterZ: -88, enterRX: 1.2, enterRY: -1.9, exitX: 18, exitY: -24, exitZ: -48, exitRX: -0.65, exitRY: 0.9, enterScale: -0.042, exitScale: -0.022, opacityFloor: 0.74 },
      { selector: '.fs-focus-rhythm', className: 'fs-motion-focus', enterX: 0, enterY: 48, enterZ: -112, enterRX: 0.9, enterRY: 0, exitX: 0, exitY: -22, exitZ: -48, exitRX: -0.48, exitRY: 0, enterScale: -0.05, exitScale: -0.02, opacityFloor: 0.75 },
      { selector: '.fs-reflect', className: 'fs-motion-reflect', enterX: -32, enterY: 50, enterZ: -84, enterRX: 1.1, enterRY: -1.7, exitX: 30, exitY: -24, exitZ: -44, exitRX: -0.6, exitRY: 1.4, enterScale: -0.04, exitScale: -0.02, opacityFloor: 0.74 },
      { selector: '.fs-improvement', className: 'fs-motion-improve', enterX: 0, enterY: 72, enterZ: -76, enterRX: 1.5, enterRY: 0, exitX: 0, exitY: -32, exitZ: -38, exitRX: -0.75, exitRY: 0, enterScale: -0.038, exitScale: -0.017, opacityFloor: 0.74 },
      { selector: '.fs-hard-hour', className: 'fs-motion-hard-hour', enterX: 0, enterY: 9, enterZ: -10, enterRX: 0, enterRY: 0, exitX: 0, exitY: -5, exitZ: -8, exitRX: 0, exitRY: 0, enterScale: -0.004, exitScale: -0.003, opacityFloor: 0.72 },
      { selector: '.fs-privacy', className: 'fs-motion-trust', enterX: 0, enterY: 7, enterZ: -7, enterRX: 0, enterRY: 0, exitX: 0, exitY: -3, exitZ: -5, exitRX: 0, exitRY: 0, enterScale: -0.002, exitScale: -0.002, opacityFloor: 0.78 },
      { selector: '.fs-paths', className: 'fs-motion-paths', enterX: 0, enterY: 62, enterZ: -92, enterRX: 2, enterRY: 0, exitX: 0, exitY: -26, exitZ: -48, exitRX: -0.8, exitRY: 0, enterScale: -0.05, exitScale: -0.024, opacityFloor: 0.72 },
      { selector: '.fs-book', className: 'fs-motion-library', enterX: 36, enterY: 58, enterZ: -128, enterRX: 1.2, enterRY: 2.3, exitX: -20, exitY: -28, exitZ: -60, exitRX: -0.7, exitRY: -1.2, enterScale: -0.06, exitScale: -0.026, opacityFloor: 0.7 },
      { selector: '.fs-steps', className: 'fs-motion-steps', enterX: -20, enterY: 68, enterZ: -82, enterRX: 1.8, enterRY: -0.8, exitX: 14, exitY: -28, exitZ: -42, exitRX: -0.7, exitRY: 0.5, enterScale: -0.045, exitScale: -0.02, opacityFloor: 0.72 },
      { selector: '.fs-questions__intro', className: 'fs-motion-faq', enterX: 0, enterY: 16, enterZ: -14, enterRX: 0.18, enterRY: 0, exitX: 0, exitY: -7, exitZ: -8, exitRX: -0.1, exitRY: 0, enterScale: -0.006, exitScale: -0.004, opacityFloor: 0.68 },
      { selector: '.fs-questions .fs-faq', className: 'fs-motion-faq', enterX: 0, enterY: 18, enterZ: -16, enterRX: 0.22, enterRY: 0, exitX: 0, exitY: -7, exitZ: -8, exitRX: -0.1, exitRY: 0, enterScale: -0.007, exitScale: -0.004, opacityFloor: 0.66 },
      { selector: '.fs-final', className: 'fs-motion-final', enterX: 0, enterY: 26, enterZ: -46, enterRX: 0.3, enterRY: 0, exitX: 0, exitY: -6, exitZ: -12, exitRX: -0.1, exitRY: 0, enterScale: -0.022, exitScale: -0.005, opacityFloor: 0.58 }
    ];
    var scenes = [];

    for (var profileIndex = 0; profileIndex < sceneProfiles.length; profileIndex += 1) {
      var profile = sceneProfiles[profileIndex];
      var matches = document.querySelectorAll(profile.selector);
      for (var matchIndex = 0; matchIndex < matches.length; matchIndex += 1) {
        matches[matchIndex].classList.add('fs-scroll-scene', profile.className);
        scenes.push({ element: matches[matchIndex], profile: profile });
      }
    }
    if (!scenes.length) return;

    var metrics = [];
    var ticking = false;

    function measure() {
      metrics = [];
      for (var m = 0; m < scenes.length; m += 1) {
        var scene = scenes[m];
        var node = scene.element;
        var top = 0;
        var cursor = node;
        while (cursor) {
          top += cursor.offsetTop || 0;
          cursor = cursor.offsetParent;
        }
        metrics.push({ element: node, profile: scene.profile, top: top, height: node.offsetHeight });
      }
    }

    function paintScenes() {
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      var scrollTop = window.scrollY || window.pageYOffset || 0;

      for (var p = 0; p < metrics.length; p += 1) {
        var metric = metrics[p];
        var activeProfile = metric.profile;
        var center = metric.top - scrollTop + metric.height * 0.5;
        var range = viewport * 0.55 + metric.height * 0.45;
        var position = Math.max(-1, Math.min(1, (center - viewport * 0.5) / range));
        var distance = Math.abs(position);
        var phase = Math.max(0, (distance - 0.1) / 0.9);
        var eased = phase * phase * (3 - 2 * phase);
        var entering = position > 0;
        var mobileFactor = window.innerWidth < 700 ? 0.56 : 1;
        var x = (entering ? activeProfile.enterX : activeProfile.exitX) * eased * mobileFactor;
        var y = (entering ? activeProfile.enterY : activeProfile.exitY) * eased * mobileFactor;
        var z = (entering ? activeProfile.enterZ : activeProfile.exitZ) * eased * mobileFactor;
        var rotateX = (entering ? activeProfile.enterRX : activeProfile.exitRX) * eased * mobileFactor;
        var rotateY = (entering ? activeProfile.enterRY : activeProfile.exitRY) * eased * mobileFactor;
        var scaleDelta = entering ? activeProfile.enterScale : activeProfile.exitScale;
        var scale = 1 + scaleDelta * eased * mobileFactor;
        var opacity = 1 - (1 - activeProfile.opacityFloor) * eased;

        metric.element.style.setProperty('--fs-scene-x', x.toFixed(2) + 'px');
        metric.element.style.setProperty('--fs-scene-y', y.toFixed(2) + 'px');
        metric.element.style.setProperty('--fs-scene-z', z.toFixed(2) + 'px');
        metric.element.style.setProperty('--fs-scene-scale', scale.toFixed(4));
        metric.element.style.setProperty('--fs-scene-opacity', opacity.toFixed(3));
        metric.element.style.setProperty('--fs-scene-rotate-x', rotateX.toFixed(3) + 'deg');
        metric.element.style.setProperty('--fs-scene-rotate-y', rotateY.toFixed(3) + 'deg');
        metric.element.style.setProperty('--fs-scene-focus', (1 - eased).toFixed(3));
      }
      ticking = false;
    }

    function requestScenePaint() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(paintScenes);
    }

    function refreshScenes() {
      measure();
      requestScenePaint();
    }

    /* Let supported browsers keep view progress entirely on the compositor. */
    if (nativeScrollTimelines) {
      root.classList.add('fs-scroll-motion-ready', 'fs-native-scroll-timeline');
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        for (var e = 0; e < entries.length; e += 1) {
          var entry = entries[e];
          if (entry.isIntersecting) {
            entry.target.classList.add('is-scene-in');
            entry.target.classList.remove('is-scene-past');
          } else {
            entry.target.classList.remove('is-scene-in');
            entry.target.classList.toggle('is-scene-past', entry.boundingClientRect.top < 0);
          }
        }
      },
      { threshold: 0.08, rootMargin: '4% 0px -8% 0px' }
    );

    measure();
    paintScenes();
    root.classList.add('fs-scroll-motion-ready');
    var i;
    for (i = 0; i < scenes.length; i += 1) {
      io.observe(scenes[i].element);
    }

    window.addEventListener('scroll', requestScenePaint, { passive: true });
    window.addEventListener('resize', refreshScenes);
    window.addEventListener('load', refreshScenes, { once: true });

    if ('ResizeObserver' in window) {
      var resizeObserver = new ResizeObserver(refreshScenes);
      for (i = 0; i < scenes.length; i += 1) {
        resizeObserver.observe(scenes[i].element);
      }
    }
  }

  function addDepthFields() {
    var fields = document.querySelectorAll(
      '.fs-hero-stage, .fs-practice .fs-phone--feature, .fs-feature-row .fs-split__object, .fs-book .fs-split__object'
    );
    if (!fields.length) return;

    for (var f = 0; f < fields.length; f += 1) {
      fields[f].classList.add('fs-depth-field');
    }

    if (nativeScrollTimelines) return;

    var ticking = false;

    function paint() {
      var viewport = window.innerHeight || document.documentElement.clientHeight;
      var compact = window.innerWidth < 700;
      var verticalLimit = compact ? 42 : 72;
      var horizontalLimit = compact ? 24 : 56;
      var depthLimit = compact ? 80 : 140;

      for (var i = 0; i < fields.length; i += 1) {
        var rect = fields[i].getBoundingClientRect();
        if (rect.bottom < -viewport * 0.2 || rect.top > viewport * 1.2) continue;
        var isPhoneField = fields[i].matches(
          '.fs-hero-stage, .fs-practice .fs-phone--feature, .fs-feature-row .fs-split__object'
        );
        var centerDelta = viewport * 0.5 - (rect.top + rect.height * 0.5);
        var position = Math.max(-1, Math.min(1, centerDelta / (viewport * 0.72 + rect.height * 0.28)));
        var distance = Math.abs(position);
        var edge = distance * distance * (3 - 2 * distance);
        var centerLift = Math.max(0, 1 - distance / 0.32);
        var direction = position < 0 ? 1 : -1;
        var side = fields[i].closest('.fs-feature-row--protect, .fs-feature-row--reflect') ? -1 : 1;
        var phoneScaleDepth = compact ? 0.16 : 0.2;
        var phoneCenterLift = compact ? 0.045 : 0.06;
        var activeDepthLimit = isPhoneField ? (compact ? 145 : 220) : depthLimit;
        var y = direction * verticalLimit * edge;
        var x = direction * side * horizontalLimit * edge;
        var z = -activeDepthLimit * edge + (isPhoneField ? centerLift * (compact ? 44 : 62) : 0);
        var tiltX = direction * -2.2 * edge;
        var tiltY = direction * side * 4.6 * edge;
        var scale = isPhoneField
          ? 1 - phoneScaleDepth * edge + phoneCenterLift * centerLift
          : 1 - (compact ? 0.075 : 0.095) * edge;

        fields[i].style.setProperty('--fs-depth-x', x.toFixed(2) + 'px');
        fields[i].style.setProperty('--fs-depth-shift', y.toFixed(2) + 'px');
        fields[i].style.setProperty('--fs-depth-z', z.toFixed(2) + 'px');
        fields[i].style.setProperty('--fs-depth-tilt', tiltX.toFixed(3) + 'deg');
        fields[i].style.setProperty('--fs-depth-turn', tiltY.toFixed(3) + 'deg');
        fields[i].style.setProperty('--fs-depth-scale', scale.toFixed(4));
        fields[i].style.setProperty('--fs-depth-focus', (1 - edge).toFixed(3));
      }
      ticking = false;
    }

    function requestPaint() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(paint);
    }

    paint();
    window.addEventListener('scroll', requestPaint, { passive: true });
    window.addEventListener('resize', requestPaint);
  }

  function markCinemaObjects() {
    var cinemaMap = [
      ['.fs-hero-stage', 'fs-cinema-hero'],
      ['.fs-why__inner, .fs-silent__inner, .fs-standard__inner', 'fs-cinema-editorial'],
      ['.fs-practice .fs-phone--feature', 'fs-cinema-plan'],
      ['.fs-protection .fs-split__object', 'fs-cinema-protect'],
      ['.fs-focus-rhythm .fs-split__object', 'fs-cinema-focus'],
      ['.fs-reflect .fs-split__object', 'fs-cinema-reflect'],
      ['.fs-improvement .fs-split__object', 'fs-cinema-improve'],
      ['.fs-book .fs-split__object', 'fs-cinema-library']
    ];

    for (var mapIndex = 0; mapIndex < cinemaMap.length; mapIndex += 1) {
      var matches = document.querySelectorAll(cinemaMap[mapIndex][0]);
      for (var matchIndex = 0; matchIndex < matches.length; matchIndex += 1) {
        matches[matchIndex].classList.add('fs-cinema-object', cinemaMap[mapIndex][1]);
      }
    }

    var splitPieces = document.querySelectorAll(
      '.fs-protection-layer, .fs-focus-showcase .fs-phone, .fs-library-product'
    );
    for (var pieceIndex = 0; pieceIndex < splitPieces.length; pieceIndex += 1) {
      splitPieces[pieceIndex].classList.add('fs-cinema-piece');
    }
  }

  function installScrollInstrument() {
    var main = document.getElementById('fs-main');
    if (!main) return;

    var meter = document.createElement('div');
    meter.className = 'fs-scroll-meter';
    meter.setAttribute('aria-hidden', 'true');
    meter.innerHTML = '<span class="fs-scroll-meter__track"><i></i></span><b></b>';
    root.appendChild(meter);

    var lastY = window.scrollY || 0;
    var ticking = false;

    function paint() {
      var y = window.scrollY || window.pageYOffset || 0;
      var max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      var progress = Math.max(0, Math.min(1, y / max));
      var meterY = progress * Math.max(1, window.innerHeight * 0.5 - 9);
      var delta = y - lastY;

      root.style.setProperty('--fs-page-progress', progress.toFixed(5));
      root.style.setProperty('--fs-page-meter-y', meterY.toFixed(2) + 'px');
      if (Math.abs(delta) > 2) {
        root.setAttribute('data-fs-scroll-direction', delta > 0 ? 'forward' : 'backward');
      }
      lastY = y;
      ticking = false;
    }

    function requestPaint() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(paint);
    }

    paint();
    window.addEventListener('scroll', requestPaint, { passive: true });
    window.addEventListener('resize', requestPaint);
  }

  function enhanceFaq() {
    if (reduce || !Element.prototype.animate) return;

    var accordions = document.querySelectorAll('.fs-questions .fs-faq');

    function settle(item) {
      item.style.removeProperty('height');
      item.style.removeProperty('overflow');
      item.removeAttribute('data-fs-faq-animating');
    }

    function animateItem(item, opening) {
      if (!item || item.hasAttribute('data-fs-faq-animating')) return;

      var summary = item.querySelector('summary');
      var answer = item.querySelector('.fs-faq__answer');
      if (!summary || !answer) {
        item.open = opening;
        return;
      }

      var startHeight = item.getBoundingClientRect().height;
      if (opening) item.open = true;

      var computed = window.getComputedStyle(item);
      var borders = parseFloat(computed.borderTopWidth) + parseFloat(computed.borderBottomWidth);
      var endHeight = opening ? item.scrollHeight : summary.offsetHeight + borders;

      item.setAttribute('data-fs-faq-animating', 'true');
      item.style.height = startHeight + 'px';
      item.style.overflow = 'hidden';

      var shell = item.animate(
        [{ height: startHeight + 'px' }, { height: endHeight + 'px' }],
        { duration: opening ? 340 : 280, easing: 'cubic-bezier(.22, 1, .36, 1)' }
      );

      answer.animate(
        opening
          ? [{ opacity: 0, transform: 'translateY(-6px)' }, { opacity: 1, transform: 'translateY(0)' }]
          : [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-4px)' }],
        { duration: opening ? 260 : 180, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'both' }
      );

      shell.onfinish = function () {
        if (!opening) item.open = false;
        settle(item);
      };
      shell.oncancel = function () {
        settle(item);
      };
    }

    for (var i = 0; i < accordions.length; i += 1) {
      (function (accordion) {
        var items = accordion.querySelectorAll('.fs-faq__item');
        for (var itemIndex = 0; itemIndex < items.length; itemIndex += 1) {
          (function (item) {
            var summary = item.querySelector('summary');
            if (!summary) return;
            summary.addEventListener('click', function (event) {
              event.preventDefault();
              var opening = !item.open;

              if (opening) {
                for (var siblingIndex = 0; siblingIndex < items.length; siblingIndex += 1) {
                  if (items[siblingIndex] !== item && items[siblingIndex].open) {
                    animateItem(items[siblingIndex], false);
                  }
                }
              }

              animateItem(item, opening);
            });
          })(items[itemIndex]);
        }
      })(accordions[i]);
    }
  }

  function densifyChrome() {
    var nav = document.querySelector('[data-fs-chrome="nav"]');
    var hero = document.querySelector('[data-fs-bookend="hero"]');
    var invitation = document.querySelector('[data-fs-bookend="final-invitation"]');
    if (!nav || !hero || !('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i += 1) {
          var entry = entries[i];
          if (entry.target === hero) {
            if (entry.isIntersecting) {
              nav.classList.remove('is-dense');
            } else {
              nav.classList.add('is-dense');
            }
          }
          if (entry.target === invitation) {
            if (entry.isIntersecting) {
              nav.classList.add('is-complete');
            } else {
              nav.classList.remove('is-complete');
            }
          }
        }
      },
      { threshold: 0.12 }
    );

    io.observe(hero);
    if (invitation) io.observe(invitation);

    var lastY = window.scrollY || 0;
    var directionTicking = false;

    function paintDirection() {
      var y = window.scrollY || 0;
      var delta = y - lastY;
      if (y < 88) {
        nav.classList.remove('is-receded');
      } else if (delta > 7) {
        nav.classList.add('is-receded');
      } else if (delta < -5) {
        nav.classList.remove('is-receded');
      }
      lastY = y;
      directionTicking = false;
    }

    window.addEventListener(
      'scroll',
      function () {
        if (directionTicking) return;
        directionTicking = true;
        window.requestAnimationFrame(paintDirection);
      },
      { passive: true }
    );
  }

  if (reduce) {
    revealAll();
  } else {
    markCinemaObjects();
    observeArrivals();
    observeScenes();
    addDepthFields();
    enhanceFaq();
    densifyChrome();
    installScrollInstrument();
  }
})();
